import { EVENT_SCHEDULE } from '../data/eventSchedule';
import { EventCreateInput, EventScheduleItem } from '../types/event';
import { requireSupabase, requireSupabasePublic, SUPABASE_EVENTS_TABLE } from './supabase';

const STORAGE_KEY = 'events_schedule';
const API_MODE = import.meta.env.VITE_RESERVATIONS_API_MODE ?? 'local';

const isSupabaseMode = () => API_MODE === 'supabase';
const ROUTE_DISTANCE_MARKER_REGEX = /\n?<!--\s*routeDistanceKm:([0-9.]+)\s*-->/i;

const getRouteDistanceFromDetails = (value: string) => {
  const match = value.match(ROUTE_DISTANCE_MARKER_REGEX);
  if (!match) return undefined;
  const distance = Number(match[1]);
  return Number.isFinite(distance) && distance > 0 ? distance : undefined;
};

export const stripRouteDistanceMarker = (value: string) =>
  value.replace(ROUTE_DISTANCE_MARKER_REGEX, '').trim();

const withRouteDistanceMarker = (details: string, routeDistanceKm?: number) => {
  const cleanDetails = stripRouteDistanceMarker(details);
  if (!routeDistanceKm || !Number.isFinite(routeDistanceKm) || routeDistanceKm <= 0) {
    return cleanDetails;
  }
  return `${cleanDetails}\n<!-- routeDistanceKm:${routeDistanceKm} -->`;
};

const isMissingRouteDistanceColumnError = (error: unknown) => {
  if (!error || typeof error !== 'object') return false;
  const record = error as Record<string, unknown>;
  const message = String(record.message ?? '');
  return (
    String(record.code ?? '') === 'PGRST204' &&
    message.toLowerCase().includes('route_distance_km')
  );
};

const isEventScheduleItem = (value: unknown): value is EventScheduleItem => {
  if (!value || typeof value !== 'object') return false;
  const item = value as Record<string, unknown>;
  return (
    typeof item.id === 'string' &&
    typeof item.category === 'string' &&
    typeof item.date === 'string' &&
    typeof item.time === 'string' &&
    typeof item.durationHours === 'number' &&
    (item.routeDistanceKm === undefined || typeof item.routeDistanceKm === 'number') &&
    typeof item.capacity === 'number' &&
    typeof item.booked === 'number' &&
    typeof item.price === 'number' &&
    typeof item.title === 'string' &&
    typeof item.summary === 'string' &&
    typeof item.details === 'string' &&
    Array.isArray(item.serviceStops)
  );
};

const readLocalEvents = (): EventScheduleItem[] => {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(EVENT_SCHEDULE));
    return EVENT_SCHEDULE;
  }

  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every(isEventScheduleItem)) {
      return parsed;
    }
  } catch {
    // Fall back to defaults when local storage is corrupted.
  }

  localStorage.setItem(STORAGE_KEY, JSON.stringify(EVENT_SCHEDULE));
  return EVENT_SCHEDULE;
};

const writeLocalEvents = (events: EventScheduleItem[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(events));
};

const toEventId = (value: unknown) => String(value ?? `evt-${Date.now()}`);

const mapSupabaseRow = (row: Record<string, unknown>): EventScheduleItem => ({
  id: toEventId(row.id),
  category: String(row.category ?? 'SUP') as EventScheduleItem['category'],
  date: String(row.date ?? ''),
  time: String(row.time ?? ''),
  durationHours: Number(row.duration_hours ?? row.durationHours ?? 0),
  routeDistanceKm:
    row.route_distance_km === null || row.routeDistanceKm === null
      ? undefined
      : row.route_distance_km !== undefined || row.routeDistanceKm !== undefined
        ? Number(row.route_distance_km ?? row.routeDistanceKm)
        : getRouteDistanceFromDetails(String(row.details ?? '')),
  capacity: Number(row.capacity ?? 0),
  booked: Number(row.booked ?? 0),
  price: Number(row.price ?? 0),
  title: String(row.title ?? ''),
  summary: String(row.summary ?? ''),
  details: String(row.details ?? ''),
  serviceStops: Array.isArray(row.service_stops)
    ? (row.service_stops as string[])
    : typeof row.serviceStops === 'string'
      ? String(row.serviceStops)
          .split('|')
          .map((item) => item.trim())
          .filter(Boolean)
      : []
});

export const listEvents = async (): Promise<EventScheduleItem[]> => {
  if (isSupabaseMode()) {
    // Use the session-free public client so that reads always go through
    // the anon role. This prevents RLS policies tied to a specific user
    // from hiding events that were inserted by another user (e.g. admin).
    const client = requireSupabasePublic();
    const { data, error } = await client
      .from(SUPABASE_EVENTS_TABLE)
      .select('*')
      .order('date', { ascending: true })
      .order('time', { ascending: true });

    if (error) {
      throw new Error(`Supabase events list failed: ${error.message}`);
    }

    return (data ?? []).map((row) => mapSupabaseRow(row as Record<string, unknown>));
  }

  return readLocalEvents();
};

export const createEvent = async (input: EventCreateInput): Promise<EventScheduleItem> => {
  if (isSupabaseMode()) {
    const supabase = requireSupabase();
    const basePayload = {
      category: input.category,
      date: input.date,
      time: input.time,
      duration_hours: input.durationHours,
      capacity: input.capacity,
      booked: 0,
      price: input.price,
      title: input.title,
      summary: input.summary,
      details: input.details,
      service_stops: input.serviceStops
    };

    let { data, error } = await supabase
      .from(SUPABASE_EVENTS_TABLE)
      .insert({
        ...basePayload,
        route_distance_km: input.routeDistanceKm
      })
      .select('*')
      .single();

    if (isMissingRouteDistanceColumnError(error)) {
      const fallbackResult = await supabase
        .from(SUPABASE_EVENTS_TABLE)
        .insert({
          ...basePayload,
          details: withRouteDistanceMarker(input.details, input.routeDistanceKm)
        })
        .select('*')
        .single();

      data = fallbackResult.data;
      error = fallbackResult.error;
    }

    if (error || !data) {
      throw new Error(`Supabase event create failed: ${error?.message ?? 'Unknown error'}`);
    }

    return mapSupabaseRow(data as Record<string, unknown>);
  }

  const nextEvent: EventScheduleItem = {
    id: `evt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    category: input.category,
    date: input.date,
    time: input.time,
    durationHours: input.durationHours,
    routeDistanceKm: input.routeDistanceKm,
    capacity: input.capacity,
    booked: 0,
    price: input.price,
    title: input.title,
    summary: input.summary,
    details: input.details,
    serviceStops: input.serviceStops
  };

  const current = readLocalEvents();
  writeLocalEvents([nextEvent, ...current]);
  return nextEvent;
};

export const deleteEvent = async (eventId: string): Promise<void> => {
  if (isSupabaseMode()) {
    const supabase = requireSupabase();
    const numericId = Number(eventId);
    const isNumeric = Number.isFinite(numericId);
    const matcher = isNumeric ? { key: 'id', value: numericId } : { key: 'id', value: eventId };

    const { error } = await supabase
      .from(SUPABASE_EVENTS_TABLE)
      .delete()
      .eq(matcher.key, matcher.value);

    if (error) {
      throw new Error(`Supabase event delete failed: ${error.message}`);
    }
    return;
  }

  const current = readLocalEvents();
  writeLocalEvents(current.filter((item) => item.id !== eventId));
};

export const updateEvent = async (
  eventId: string,
  input: EventCreateInput
): Promise<EventScheduleItem> => {
  if (isSupabaseMode()) {
    const supabase = requireSupabase();
    const numericId = Number(eventId);
    const isNumeric = Number.isFinite(numericId);
    const matcher = isNumeric ? { key: 'id', value: numericId } : { key: 'id', value: eventId };

    const { data, error } = await supabase
      .from(SUPABASE_EVENTS_TABLE)
      .update({
        category: input.category,
        date: input.date,
        time: input.time,
        duration_hours: input.durationHours,
        route_distance_km: input.routeDistanceKm,
        capacity: input.capacity,
        price: input.price,
        title: input.title,
        summary: input.summary,
        details: input.details,
        service_stops: input.serviceStops
      })
      .eq(matcher.key, matcher.value)
      .select('*')
      .single();

    if (isMissingRouteDistanceColumnError(error)) {
      const fallbackResult = await supabase
        .from(SUPABASE_EVENTS_TABLE)
        .update({
          category: input.category,
          date: input.date,
          time: input.time,
          duration_hours: input.durationHours,
          capacity: input.capacity,
          price: input.price,
          title: input.title,
          summary: input.summary,
          details: withRouteDistanceMarker(input.details, input.routeDistanceKm),
          service_stops: input.serviceStops
        })
        .eq(matcher.key, matcher.value)
        .select('*')
        .single();

      if (fallbackResult.error || !fallbackResult.data) {
        throw new Error(`Supabase event update failed: ${fallbackResult.error?.message ?? 'Unknown error'}`);
      }

      return mapSupabaseRow(fallbackResult.data as Record<string, unknown>);
    }

    if (error || !data) {
      throw new Error(`Supabase event update failed: ${error?.message ?? 'Unknown error'}`);
    }

    return mapSupabaseRow(data as Record<string, unknown>);
  }

  const current = readLocalEvents();
  const existing = current.find((item) => item.id === eventId);
  if (!existing) {
    throw new Error('Event not found.');
  }

  const updated: EventScheduleItem = {
    ...existing,
    category: input.category,
    date: input.date,
    time: input.time,
    durationHours: input.durationHours,
    routeDistanceKm: input.routeDistanceKm,
    capacity: input.capacity,
    price: input.price,
    title: input.title,
    summary: input.summary,
    details: input.details,
    serviceStops: input.serviceStops
  };

  writeLocalEvents(current.map((item) => (item.id === eventId ? updated : item)));
  return updated;
};
